// SubscriptionGate — hard-paywall wrapper for the native iOS app.
//
// Wraps every authenticated app route. Blocks non-subscribed users
// behind the NativePaywall until they start a subscription (or free
// trial via RevenueCat + Apple IAP).
//
// Design contract
// ---------------
// * `subscription_status` is the single source of truth. Values that
//   grant access: "active", "trialing", "cancellation_pending"
//   (Apple's cancel-but-still-in-paid-period state — respects the
//   contract with the user until expiration).
// * Only enforces on **native iOS**. Web users pay via Stripe with
//   their own paywall on /checkout — we don't want to break that.
// * Renders as a full-screen overlay above the app so users can't
//   dismiss it with back gestures. `overflow: hidden` on `<html>`
//   + `position: fixed` on the overlay = zero visible app content.
// * Shows a small "Log out" affordance so a user who signed up with
//   the wrong email can escape — otherwise the app is un-un-installable.

import { useEffect, useState } from "react";
import Spinner from "./Spinner";
import { LogOut, RefreshCw, CreditCard, AlertCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n.jsx";
import { isNativeApp, nativePlatform, openExternal } from "@/lib/native";
import NativePaywall from "@/components/NativePaywall";
import AndroidStripeRedirect from "@/components/AndroidStripeRedirect";
import { logOutRevenueCat, syncPurchases, configureRevenueCat } from "@/lib/revenuecat";
import { toast } from "sonner";
import api from "@/lib/api";

// GCC-6 iOS Stripe routing. For users geo-detected in these countries
// we show the SAME Stripe web-checkout flow Android uses instead of
// Apple IAP — saves the Apple 15-30% commission. Decision is made
// server-side (`/api/geo/paywall-decision`) so a VPN'd reviewer with
// a non-GCC Apple ID always falls through to the real IAP paywall.
// Admin can kill-switch this globally via /admin/gcc-stripe/toggle
// if Apple flags the app during review.
const GCC_STRIPE_CACHE_KEY = "calofay:gcc_gateway_cache_v1";

const ACCESS_STATUSES = new Set(["active", "trialing", "cancellation_pending"]);

// Hard-coded production testing-email set. Must stay in sync with
// `TESTING_EMAILS` in `backend/core.py`. These accounts are always
// treated as non-subscribed so the owner can re-run the full paywall
// flow in production without needing a fresh email every time.
//
// SubscriptionGate uses this to skip the reconcile+poll cycle so the
// 14 s "Activating your subscription…" spinner never traps the test
// account on Android after a real Stripe webhook has flipped the DB
// to active (we deliberately mask that at the API layer; polling for
// it would hang the test flow).
const TESTING_EMAILS = new Set(["ghamrawi2+testing@gmail.com"]);
function isTestingEmail(email) {
  return !!email && TESTING_EMAILS.has(String(email).trim().toLowerCase());
}

function hasAccess(user) {
  if (!user) return false;
  return ACCESS_STATUSES.has(String(user.subscription_status || "").toLowerCase());
}

export default function SubscriptionGate({ children }) {
  const { user, logout, refresh } = useAuth();
  const { lang } = useI18n();
  const en = lang === "en";
  const [checking, setChecking] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // iOS GCC → Stripe routing. `null` while deciding, "apple" or
  // "stripe" after /api/geo/paywall-decision resolves. Cached in
  // localStorage for 24h so repeat opens don't re-hit the server.
  const [iosGateway, setIosGateway] = useState(() => {
    try {
      const raw = localStorage.getItem(GCC_STRIPE_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.at && Date.now() - parsed.at < 24 * 60 * 60 * 1000) {
        return parsed.gateway;
      }
    } catch (_) {}
    return null;
  });

  // Enforce on iOS AND Android native. Web/preview keeps existing
  // Stripe-based paywall on /checkout.
  const enforce = isNativeApp() && (nativePlatform() === "ios" || nativePlatform() === "android");

  // Manual "Refresh status" — same reconcile+refresh cycle as the initial
  // effect, but user-triggered when they've just paid/redeemed a code
  // and want an immediate re-check without waiting for the webhook.
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      try { await api.post("/revenuecat/reconcile"); } catch (_) { /* server-side sync failed — the refresh() below still re-reads whatever DB has */ }
      try { await refresh(); } catch (_) {}
      toast.success(en ? "Status updated" : "تم تحديث الحالة");
    } finally {
      setRefreshing(false);
    }
  };

  // After a successful IAP the RevenueCat webhook takes 5-30s to hit
  // our backend (worse in sandbox). We poll reconcile+refresh with
  // BACKOFF and EXIT EARLY the moment `subscription_status` flips to
  // an access status — so a happy-path purchase resolves in ~500 ms,
  // not the full 14 s. Only slow-webhook sandbox purchases will burn
  // the whole window.
  const handlePurchaseSuccess = async () => {
    const delays = [400, 800, 1500, 2500, 4000, 5000]; // up to ~14 s
    let confirmed = false;
    for (const wait of delays) {
      try { await api.post("/revenuecat/reconcile"); } catch (_) {}
      let u = null;
      try { u = await refresh(); } catch (_) {}
      if (u && ACCESS_STATUSES.has(String(u.subscription_status || "").toLowerCase())) {
        confirmed = true;
        break;
      }
      await new Promise((r) => setTimeout(r, wait));
    }
    if (typeof window !== "undefined") {
      try {
        const me = await api.get("/auth/me");
        const u = me?.data || {};
        window.location.href = u.onboarding_completed ? "/app" : "/onboarding";
      } catch (_) {
        // Backend refused — best-effort fall back to /app so AppShell
        // can decide whether to bounce to /onboarding.
        window.location.href = "/app";
      }
    }
  };

  // Full logout — must also un-scope RevenueCat so the next user who
  // signs in on this device doesn't inherit the previous customer's
  // entitlements (RC caches the appUserID persistently).
  //
  // On Capacitor iOS the SubscriptionGate can stay mounted after
  // setUser(null) because the underlying webview keeps history, so we
  // force a hard reload to /login to guarantee the auth state fully
  // resets. Same behaviour on web — a clean bounce is more reliable
  // than trusting router transitions.
  const handleLogout = async () => {
    try { await logOutRevenueCat(); } catch (_) {}
    try { await logout(); } catch (_) {}
    try { localStorage.removeItem("calofay_token"); localStorage.removeItem("calofay_refresh"); } catch (_) {}
    // Small delay so the toast (if any) can render before navigation.
    setTimeout(() => { window.location.href = "/login"; }, 100);
  };

  useEffect(() => {
    let alive = true;
    async function reconcile() {
      if (!enforce || !user) {
        if (alive) setChecking(false);
        return;
      }
      // Testing-email bypass — skip the entire reconcile+poll cycle.
      // Backend masks their subscription state to "none" by design, so
      // reconciling against RevenueCat and polling /auth/me would just
      // burn 14 s on the "Activating your subscription…" spinner
      // before falling through to the paywall anyway. Jump straight
      // to the paywall render so the owner can re-test the flow.
      if (isTestingEmail(user.email)) {
        if (alive) setChecking(false);
        return;
      }
      // ── Auto-restore for users with an existing store subscription ──
      // If the DB says this user is NOT active, they may still have an
      // active Google Play / Apple subscription on this device that was
      // purchased under a DIFFERENT app_user_id (bought on another
      // device, reinstalled after logout, or signed up with a new
      // Calofay account on the same store account). In that case
      // /revenuecat/reconcile alone would 404 — RC has no record under
      // the CURRENT app_user_id.
      //
      // `syncPurchases()` pushes the local Play/Apple receipt to RC
      // under the current app_user_id, which triggers RC's server-
      // side transfer flow (subscription now belongs to this user
      // record). AFTER that succeeds, the very next reconcile call
      // sees the subscription and flips the user to "active" —
      // exactly what the manual "Restore purchases" tap was doing
      // but now automatic, so users never see the "لا يوجد اشتراك
      // سابق" paywall when they actually DO have an active sub.
      //
      // We ONLY run this for non-active users so we don't burn the
      // native bridge on every SubscriptionGate mount for the happy
      // path (already-subscribed users).
      if (!hasAccess(user)) {
        try {
          await configureRevenueCat(user.id);
          await syncPurchases();
        } catch (_) { /* non-fatal, reconcile below still runs */ }
      }
      // Server-side double-check via RevenueCat REST v2. Guards against
      // the case where a webhook was dropped and the client thinks it's
      // still non-premium after a completed purchase.
      try { await api.post("/revenuecat/reconcile"); } catch (_) {}
      let latest = user;
      try { latest = (await refresh()) || user; } catch (_) {}

      // Android Stripe webhook can lag 5-30s after a successful
      // payment. If we fall through to AndroidStripeRedirect right
      // now the user sees a card that looks identical to the N10
      // onboarding promo screen they just came from and thinks the
      // payment bounced them back. Poll with backoff for ~14s before
      // we give up and render the paywall. iOS/Apple IAP has its
      // own instant-receipt path so this only runs for Android.
      if (!hasAccess(latest) && nativePlatform() === "android") {
        const delays = [800, 1500, 2500, 4000, 5000]; // ~14s
        for (const wait of delays) {
          await new Promise((r) => setTimeout(r, wait));
          if (!alive) return;
          try { await api.post("/revenuecat/reconcile"); } catch (_) {}
          try { latest = (await refresh()) || latest; } catch (_) {}
          if (hasAccess(latest)) break;
        }
      }
      if (alive) setChecking(false);
    }
    reconcile();
    return () => { alive = false; };
  // We only reconcile once per user session — depending on user?.id is
  // cheaper than depending on the full user object and prevents infinite
  // loops when refresh() rewrites the user reference identity.
  }, [enforce, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve the paywall gateway for iOS users ONCE per session.
  // Server-authoritative: a VPN'd Apple reviewer with a non-GCC Apple
  // ID still gets "apple" because our GeoIP sees their real exit IP.
  // Decision is cached in localStorage for 24h to avoid a round-trip
  // on every SubscriptionGate mount — short enough that an admin
  // kill-switch flip propagates to the field within a day.
  useEffect(() => {
    if (!enforce) return;
    if (nativePlatform() !== "ios") return;
    if (iosGateway) return; // already decided this session
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/geo/paywall-decision");
        const g = data?.gateway === "stripe" ? "stripe" : "apple";
        if (!alive) return;
        setIosGateway(g);
        try {
          localStorage.setItem(
            GCC_STRIPE_CACHE_KEY,
            JSON.stringify({ gateway: g, country: data?.country, at: Date.now() }),
          );
        } catch (_) {}
      } catch (_) {
        // Fail-safe: on any error default to Apple IAP so an Apple
        // reviewer never sees Stripe just because our geo endpoint
        // hiccupped.
        if (alive) setIosGateway("apple");
      }
    })();
    return () => { alive = false; };
  }, [enforce, iosGateway]);

  if (!enforce) return children;
  if (checking) {
    return (
      <div className="fixed inset-0 cf-boot-bg flex items-center justify-center z-[100]" data-testid="subgate-loading" dir={en ? "ltr" : "rtl"}>
        <Spinner size="lg" bare />
      </div>
    );
  }
  // Signed-out users hit this gate right after tapping "Log out" —
  // don't show them a paywall, bounce them to /login. Without this
  // the whole app looks broken ("Couldn't reach the App Store")
  // because NativePaywall tries to configure RevenueCat with a null
  // user id and getOfferings() silently fails.
  if (!user) {
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.replace("/login");
    }
    return null;
  }
  if (hasAccess(user)) return children;

  // Bug fix: if the user is already navigating to a public checkout/
  // activation URL, don't block it with the paywall overlay. Without
  // this escape hatch, the "اذهب إلى صفحة الاشتراك" button on the
  // GCC-Stripe discount card would call `nav("/checkout?src=ios_gcc")`
  // → URL changes but SubscriptionGate remains mounted with status
  // still "none" → overlay keeps covering the Stripe form so the
  // user sees "nothing happens" after tapping.
  if (typeof window !== "undefined") {
    const p = window.location.pathname || "";
    if (
      p.startsWith("/checkout") ||
      p.startsWith("/payment-success") ||
      p.startsWith("/activate") ||
      p.startsWith("/forgot-password") ||
      p.startsWith("/app/renew")
    ) {
      return children;
    }
  }

  // Apple gives users a 16-day grace period while retrying a failed
  // payment (Guideline: "billing retry state"). Showing them the
  // full paywall — as if they had never subscribed — is a documented
  // conversion killer: they think we double-charged them, panic,
  // and churn. Instead we render a smaller "Update your payment
  // method" screen that deep-links into iOS Settings > Subscriptions.
  const isPastDue = String(user.subscription_status || "").toLowerCase() === "past_due";
  const isAndroid = nativePlatform() === "android";
  const isStripePastDue = !String(user.subscription_source || "stripe").startsWith("revenuecat");
  const storeName = isStripePastDue ? (en ? "your card" : "بطاقتك") : isAndroid ? (en ? "Google" : "جوجل") : "Apple";
  const storeSettingsUrl = isAndroid
    ? "https://play.google.com/store/account/subscriptions"
    : "https://apps.apple.com/account/billing";
  if (isPastDue) {
    return (
      <div
        className="fixed inset-0 bg-white z-[100] overflow-y-auto"
        style={{ height: "100dvh" }}
        data-testid="subgate-past-due"
      >
        <div className="max-w-md mx-auto px-6 pt-16 pb-6" dir={en ? "ltr" : "rtl"}>
          <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="w-8 h-8 text-amber-500" strokeWidth={2.5} />
          </div>
          <h1 className="text-2xl font-black text-center text-neutral-900 mb-2">
            {en ? "Payment issue" : "مشكلة في الدفع"}
          </h1>
          <p className="text-sm text-center text-neutral-500 leading-relaxed mb-8">
            {isStripePastDue
              ? (en
                ? "We couldn't charge your card for the last payment. Update your card to keep your access."
                : "تعذّر خصم الدفعة الأخيرة من بطاقتك. حدّث بيانات البطاقة للحفاظ على وصولك.")
              : en
              ? `${storeName} couldn't process your last payment. Your subscription stays active while we retry — please update your payment method in ${storeName} settings to keep your access.`
              : `تعذر على ${storeName} إتمام الدفعة الأخيرة. اشتراكك لا يزال نشطاً بينما نعيد المحاولة — يرجى تحديث طريقة الدفع في إعدادات ${storeName} للحفاظ على وصولك.`}
          </p>
          <button
            onClick={() => (isStripePastDue ? window.location.assign("/app/renew") : openExternal(storeSettingsUrl))}
            className="w-full orange-gradient text-white font-bold py-4 rounded-full shadow-floating active:scale-[0.98] transition-transform flex items-center justify-center gap-2 mb-3"
            data-testid="subgate-past-due-update"
          >
            <CreditCard className="w-4 h-4" />
            {en ? "Update payment method" : "تحديث طريقة الدفع"}
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="w-full text-center py-3 text-sm text-neutral-500 hover:text-brand-500 disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="subgate-past-due-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {en ? "I've updated it — refresh status" : "قمت بالتحديث — تحديث الحالة"}
          </button>
          <div className="mt-8 flex items-center justify-center">
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-xs text-neutral-400 hover:text-red-500"
              data-testid="subgate-past-due-logout"
            >
              <LogOut className="w-3.5 h-3.5" />
              {en ? "Log out" : "تسجيل خروج"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Blocked — render paywall as a full-screen overlay above the app.
  return (
    <div
      className="fixed inset-0 bg-white z-[100] overflow-y-auto"
      style={{ height: "100dvh" }}
      data-testid="subgate-locked"
    >
      <div className="max-w-md mx-auto px-5 pt-8 pb-6" dir={en ? "ltr" : "rtl"}>
        {/* Brand wordmark removed per owner request — the promo
            card below carries its own strong visual (ticket disc +
            C10/N10 code) and the small "كالوفاي" tag above it was
            just visual noise. */}

        {isAndroid || iosGateway === "stripe" ? (
          // Android: Google Play IAP paused (tax form pending).
          // iOS/GCC: server-side geo decision routed this user to
          // the Stripe path to save the Apple commission. Same
          // component for both — N10 code auto-applied, embedded
          // in the WebView, no system browser. On the next
          // SubscriptionGate mount after payment, their
          // `subscription_status` flips to "active" and this
          // overlay disappears.
          <AndroidStripeRedirect />
        ) : (
          <NativePaywall onSuccess={handlePurchaseSuccess} />
        )}

        {/* Small escape hatch — user who signed up with a wrong account
            can log out and try again, otherwise the app is a trap. */}
        <div className="mt-8 flex items-center justify-center gap-6 text-xs text-neutral-400">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 hover:text-brand-500 disabled:opacity-50"
            data-testid="subgate-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {en ? "Refresh status" : "تحديث الحالة"}
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 hover:text-red-500"
            data-testid="subgate-logout"
          >
            <LogOut className="w-3.5 h-3.5" />
            {en ? "Log out" : "تسجيل خروج"}
          </button>
        </div>
      </div>
    </div>
  );
}
